// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { SelectChangeEvent } from '@mui/material/Select';
import { type WeightUnit, getCurrencySymbol, getWeightUnitOptions } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField } from '../../lib/base';
import { ClearIcon } from '../../lib/icons';
import { shortenWeightUnit, validateNumInput } from '../../utils';

export interface SizeFormInput {
  readonly size: string;
  readonly unit: WeightUnit;
  readonly price: string;
  readonly priceValidation: string;
  readonly sizeValidation: string;
}

export interface Props {
  readonly disableFields: boolean;
  readonly index: number;
  readonly formKey: number;
  readonly values: SizeFormInput;
  readonly onChangeField: (
    index: number,
    fieldName: keyof SizeFormInput,
    value: string | number | null,
    validationFieldName?: 'sizeValidation' | 'priceValidation',
  ) => void;
  readonly onRemove: (index: number) => void;
  // readonly onEnter: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export default function NewSizeForm({ index, disableFields, onChangeField, values, onRemove }: Props) {
  const { preferredWeightUnit, currencyCode } = useUserContext();
  const onUnitChange = (e: SelectChangeEvent<string | null>) => {
    onChangeField(index, 'unit', e.target.value);
  };
  const onSizeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: true,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => onChangeField(index, 'sizeValidation', msg),
    });
    onChangeField(index, 'size', e.target.value);
  };
  const onSizeBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: true,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => onChangeField(index, 'sizeValidation', msg),
    });
  };
  const onPriceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: false,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => onChangeField(index, 'priceValidation', msg),
    });
    onChangeField(index, 'price', e.target.value);
  };
  const onPriceBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: false,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => onChangeField(index, 'priceValidation', msg),
    });
  };

  return (
    <Box gap={2} sx={{ border: 1, borderColor: 'grey.400', borderRadius: '4px', p: 2 }}>
      <Grid
        container
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
        alignItems="center"
        gap={2}
      >
        <Grid
          flexGrow="2"
          size={{ xs: 12, sm: 'grow' }}
          container
          display="flex"
          flexWrap="nowrap"
          gap={2}
          alignItems="center"
        >
          <Grid flexGrow="1">
            <TextField
              aria-describedby="size"
              id="size"
              label="Size"
              value={values.size}
              onChange={onSizeChange}
              onBlur={onSizeBlur}
              hasSubtext
              subtext={values.sizeValidation}
              error={values.sizeValidation !== ''}
              // onEnter={onEnter}
              required
              numberMin={0}
              type="number"
              inputMode="decimal"
              disabled={disableFields}
            />
          </Grid>
          <Grid sx={{ minWidth: 'unset' }}>
            <FormControl fullWidth>
              <InputLabel id="units-selector">Units</InputLabel>
              <Select
                aria-describedby="units-selector"
                id="units-selector"
                labelId="units-selector"
                label="Units"
                value={values.unit}
                onChange={onUnitChange}
                // onEnter={onEnter}
                required={true}
                autoFocus={false}
                disabled={disableFields}
              >
                {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                  return (
                    <MenuItem key={sizeUnit} value={sizeUnit}>
                      {shortenWeightUnit(sizeUnit)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid sx={{ display: { xs: 'block', sm: 'none' } }}>
            <IconButton aria-label="remove-size" onClick={() => onRemove(index)}>
              <ClearIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid flexGrow="1" size={{ xs: 12, sm: 'auto' }}>
          <TextField
            aria-describedby="price"
            id="price"
            label="Price (optional)"
            value={values.price}
            onChange={onPriceChange}
            onBlur={onPriceBlur}
            startAdornment={<span style={{ marginRight: 4 }}>{getCurrencySymbol(currencyCode)}</span>}
            hasSubtext
            subtext={values.priceValidation}
            error={values.priceValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            numberStep={0.01}
            disabled={disableFields}
          />
        </Grid>
        <Grid sx={{ display: { xs: 'none', sm: 'block' } }}>
          <IconButton aria-label="remove-size" onClick={() => onRemove(index)}>
            <ClearIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
}
